'use client'
import React, { useImperativeHandle, useRef, useState, useEffect } from 'react'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger'
import type { HeroBannerProps } from './types.js'
import { useMediaQuery } from 'react-responsive'
import { useInView } from 'react-intersection-observer'
import ScrollLoadVideo from '../../helpers/ScrollLoadVideo.js'
import { Button, Heading, Picture, Text, Countdown } from '../../components/index.js'
import { cn } from '../../helpers/index.js'
import { cva } from 'class-variance-authority'
import { withLayout } from '../../shared/Styles.js'
import { useExposure } from '../../hooks/useExposure.js'
import { trackUrlRef } from '../../shared/trackUrlRef.js'
import { getLocalizedPath } from '../../helpers/utils.js'
import { useAiuiContext } from '../AiuiProvider/index.js'
import { sizeMap } from '../../components/button.js'
import { VideoModal } from '../VideoModal/index.js'

const componentType = 'image'
const componentName = 'hero_banner'

// CVA variants for align
const contentVariants = cva(
  'hero-banner-content lg-desktop:gap-[32px] absolute top-24 z-10 flex w-full flex-col gap-[24px]',
  {
    variants: {
      align: {
        left: 'tablet:px-[32px] laptop:top-1/2 laptop:px-[64px] lg-desktop:px-[calc(50%-832px)] laptop:-translate-y-1/2 left-0 px-[16px]',
        center:
          'tablet:top-[84px] laptop:top-[76px] desktop:top-[176px] lg-desktop:top-[192px] left-1/2 top-[84px] -translate-x-1/2 translate-y-0 items-center text-center',
      },
    },
    defaultVariants: {
      align: 'left',
    },
  }
)

const textVariants = cva(
  'hero-banner-wrap-text lg-desktop:max-w-[824px] desktop:max-w-[648px] laptop:max-w-[440px] tablet:max-w-[704px] max-w-[358px]',
  {
    variants: {
      align: {
        left: 'laptop:text-left',
        center: 'text-center',
      },
    },
    defaultVariants: {
      align: 'left',
    },
  }
)

const buttonGroupVariants = cva('hero-banner-button-group lg-desktop:gap-3 flex items-center gap-2', {
  variants: {
    align: {
      left: 'laptop:justify-start',
      center: 'justify-center',
    },
  },
  defaultVariants: {
    align: 'left',
  },
})

const iconGroupVariants = cva('hero-banner-icon-group flex items-center gap-2', {
  variants: {
    align: {
      left: 'justify-start',
      center: 'justify-center',
    },
  },
  defaultVariants: {
    align: 'left',
  },
})

export type HeroBannerSemanticName =
  | 'root'
  | 'title'
  | 'subtitle'
  | 'buttonGroup'
  | 'primaryButton'
  | 'secondaryButton'
  | 'captionGroup'

const PlayButtonAppendIcon = ({ size = 'base' }: { size: 'base' | 'lg' | 'sm' }) => {
  const { width, height } = sizeMap[size]
  return (
    <svg width={width} height={height} viewBox="0 0 20 20" fill="currentcolor" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M13.9599 9.30662C14.4547 9.63647 14.4547 10.3635 13.9599 10.6934L6.29558 15.8029C5.74179 16.1721 5 15.7751 5 15.1096V4.89042C5 4.22484 5.74179 3.82785 6.29558 4.19705L13.9599 9.30662Z"
        fill="currentcolor"
      />
    </svg>
  )
}

const HeroBanner = React.forwardRef<
  HTMLDivElement,
  HeroBannerProps & {
    classNames?: Partial<Record<HeroBannerSemanticName, string>>
  }
>(({ data, className, classNames = {}, onSecondaryClick, onPrimaryClick, ...rest }, ref) => {
  const { locale } = useAiuiContext()

  // 包装链接，自动添加 locale 前缀
  const localizeUrl = (url?: string) => {
    if (!url || !locale) return url
    return getLocalizedPath(url, locale)
  }

  const {
    label,
    title,
    subtitle,
    endDate,
    endDate_tz,
    dateFormat,
    pcImage,
    padImage,
    mobileImage,
    pcVideo,
    padVideo,
    mobileVideo,
    isShowVideo,
    isVideoLoop = true,
    primaryButton,
    secondaryButton,
    theme = 'light',
    size = 'default',
    titleSize,
    caption = [],
    blockLink,
    iconArray,
    align = 'left',
  } = data

  const isMobile = useMediaQuery({ query: '(max-width: 768px)' })
  const isPad = useMediaQuery({ query: '(max-width: 1024px)' })
  const [visible, setVisible] = useState<boolean>(false)
  const { ref: inViewRef, inView } = useInView()
  const scrollTriggerRef = useRef<ScrollTrigger | null>(null)
  const bgTriggerRef = useRef<ScrollTrigger | null>(null)
  const boxTriggerRef = useRef<ScrollTrigger | null>(null)

  const bgRef = useRef<HTMLImageElement>(null)
  const boxRef = useRef<HTMLDivElement>(null)

  useExposure(boxRef, {
    componentType,
    componentName,
    componentTitle: title,
    componentDescription: subtitle,
  })

  useImperativeHandle(ref, () => boxRef.current as HTMLDivElement)

  useEffect(() => {
    gsap.registerPlugin(ScrollTrigger)
    function gsapResize() {
      if (!bgRef.current) return
      const clientHeight = boxRef.current?.clientHeight || 100
      const screenHeight = window.innerHeight

      if (screenHeight <= clientHeight) {
        scrollTriggerRef.current = ScrollTrigger.create({
          trigger: boxRef.current,
          start: 'top bottom',
          end: 'bottom top',
          scrub: true,
          onUpdate: (self: any) => {
            const base = 40
            const value = self.progress * base - base / 2
            gsap.set(bgRef.current, { yPercent: value })
          },
        })
      } else {
        boxTriggerRef.current = ScrollTrigger.create({
          trigger: boxRef.current,
          start: 'top bottom',
          end: 'bottom bottom',
          scrub: true,
          onUpdate: (self: any) => {
            const base = 20
            const value = self.progress * base - base
            gsap.set(bgRef.current, { yPercent: value })
          },
        })
        bgTriggerRef.current = ScrollTrigger.create({
          trigger: boxRef.current,
          start: 'top top',
          end: 'bottom top',
          scrub: true,
          onUpdate: (self: any) => {
            const base = 20
            const value = self.progress * base
            gsap.set(bgRef.current, { yPercent: value })
          },
        })
      }
    }
    if (inView) gsapResize()
    return () => {
      // ScrollTrigger.getAll().forEach((t: any) => t.kill())
      scrollTriggerRef.current && scrollTriggerRef.current.kill()
      boxTriggerRef.current && boxTriggerRef.current.kill()
      bgTriggerRef.current && bgTriggerRef.current.kill()
    }
  }, [inView])

  return (
    <div {...rest} ref={inViewRef} data-ui-component-id="HeroBanner">
      <div
        ref={boxRef}
        className={cn(
          theme === 'dark' ? 'aiui-dark' : '',
          'text-info-primary relative  w-full overflow-hidden',
          {
            'lg-desktop:aspect-[1920/930] desktop:aspect-[1440/700] laptop:aspect-[1024/520] tablet:aspect-[768/660] aspect-[390/660]':
              size === 'default',
            'lg-desktop:aspect-[1920/800] desktop:aspect-[1440/640] laptop:aspect-[1024/480] tablet:aspect-[768/560] aspect-[390/560]':
              size === 'sm',
          },
          className
        )}
      >
        {blockLink && (
          <a
            className="absolute inset-0 z-10"
            href={trackUrlRef(localizeUrl(blockLink), `${componentType}_${componentName}`)}
            data-headless-type-name={`${componentType}#${componentName}`}
            data-headless-title-desc-button={`${title}#${subtitle}`}
            tabIndex={-1}
            aria-hidden="true"
            aria-label={title}
          ></a>
        )}
        <div ref={bgRef} className={cn('absolute left-0 top-0 size-full')}>
          {isShowVideo ? (
            <ScrollLoadVideo
              poster={isMobile ? mobileImage?.url : isPad ? padImage?.url || mobileImage?.url : pcImage?.url}
              src={
                isMobile
                  ? (mobileVideo?.url as string)
                  : isPad
                    ? (padVideo?.url as string) || (mobileVideo?.url as string)
                    : (pcVideo?.url as string)
              }
              className="laptop:w-full h-full"
              videoClassName="h-full object-cover"
              muted
              loop={isVideoLoop}
              playsInline
            />
          ) : (
            <Picture
              className="laptop:w-full h-full"
              imgClassName="h-full object-cover"
              loading="eager"
              fetchPriority="high"
              alt={pcImage?.alt || ''}
              source={`${pcImage?.url || ''} , ${padImage?.url ?? (mobileImage?.url || '')} 1024, ${mobileImage?.url || ''} 767`}
            />
          )}
        </div>

        {/* 内容区域 */}
        <div className={contentVariants({ align })}>
          <div className={textVariants({ align })}>
            {label && (
              <Text
                size={2}
                as="p"
                className={cn('hero-banner-label font-heading lg-desktop:text-[18px] desktop:text-base text-sm')}
                html={label}
              />
            )}
            {title && (
              <Heading
                as={titleSize === '4' ? 'h1' : 'h2'}
                html={title}
                className={cn('hero-banner-title', classNames.title)}
                size={titleSize ? (Number(titleSize || '5') as any) : size === 'sm' ? 4 : 5}
              />
            )}
            {subtitle && (
              <Text
                as="p"
                size={2}
                className={cn(
                  'hero-banner-subtitle font-heading lg-desktop:text-[18px] desktop:text-base laptop:mt-2 lg-desktop:mt-4 mt-1 text-sm'
                )}
                html={subtitle}
              />
            )}
            {endDate && (
              <div className="mt-3">
                <Countdown
                  endDate={endDate}
                  endDate_tz={endDate_tz}
                  dateFormat={dateFormat}
                  variant="spacious"
                  align={align}
                />
              </div>
            )}
          </div>
          {/* 按钮组 */}
          <div className={buttonGroupVariants({ align })}>
            {secondaryButton?.isShowPlayVideoButton && secondaryButton?.playVideoButtonText ? (
              <Button
                onClick={() => setVisible(true)}
                size="lg"
                variant="secondary"
                className="hero-banner-play-video-button"
                data-headless-type-name={`${componentType}#${componentName}`}
                data-headless-title-desc-button={`${title}#${subtitle}#${secondaryButton?.playVideoButtonText}`}
              >
                {secondaryButton?.playVideoButtonText} <PlayButtonAppendIcon size="lg" />
              </Button>
            ) : secondaryButton?.text ? (
              <Button
                aria-label={title ?? subtitle}
                size="lg"
                variant="secondary"
                className="hero-banner-secondary-button"
                as={secondaryButton?.isCustomSecondaryButton ? 'button' : 'a'}
                href={trackUrlRef(localizeUrl(secondaryButton?.link), `${componentType}_${componentName}`)}
                onClick={e =>
                  secondaryButton?.isCustomSecondaryButton &&
                  onSecondaryClick?.(data, e, secondaryButton?.customSecondaryEventId)
                }
                data-headless-type-name={`${componentType}#${componentName}`}
                data-headless-title-desc-button={`${title}#${subtitle}#${secondaryButton?.text}`}
              >
                {secondaryButton?.text}
                <span className="sr-only">{title ?? subtitle}</span>
              </Button>
            ) : null}
            {primaryButton && primaryButton.text && (
              <Button
                aria-label={title ?? subtitle}
                size="lg"
                variant="primary"
                className="hero-banner-primary-button"
                as={primaryButton?.isCustomPrimaryButton ? 'button' : 'a'}
                href={trackUrlRef(localizeUrl(primaryButton.link), `${componentType}_${componentName}`)}
                onClick={e =>
                  primaryButton?.isCustomPrimaryButton && onPrimaryClick?.(data, e, primaryButton?.customPrimaryEventId)
                }
                data-headless-type-name={`${componentType}#${componentName}`}
                data-headless-title-desc-button={`${title}#${subtitle}#${primaryButton?.text}`}
              >
                {primaryButton.text}
              </Button>
            )}
          </div>
          <div className={iconGroupVariants({ align })}>
            {iconArray?.map(icon => (
              <div key={icon?.pcImage?.url || icon?.pcImage?.alt} className="h-12">
                <Picture
                  className="laptop:w-full h-full"
                  imgClassName="h-full object-cover"
                  loading="eager"
                  alt={icon?.pcImage?.alt || ''}
                  source={icon?.pcImage?.url}
                />
              </div>
            ))}
          </div>
        </div>

        {/* 底部产品列表 */}
        {caption.length > 0 && (
          <div
            className={cn(
              'hero-banner-caption-group laptop:gap-3 tablet:px-[32px] laptop:px-[64px] lg-desktop:px-[calc(50%-832px)] desktop:pb-[24px] absolute bottom-0 z-10 flex items-stretch gap-2 px-[16px] pb-[16px]',
              classNames.captionGroup
            )}
          >
            {caption.map((c, index) => (
              <React.Fragment key={c.title}>
                <Text
                  size={2}
                  className={cn(
                    'hero-banner-product-text tablet:w-[108px] loptop:w-[150px] desktop:w-[156px] lg-desktop:w-[180px] laptop:text-[14px] flex-1 text-[12px]'
                  )}
                  html={c.title}
                />
                {index < caption.length - 1 && <div className={cn('bg-info-primary w-px')} />}
              </React.Fragment>
            ))}
          </div>
        )}

        {/* 视频弹窗 */}
        {visible && (
          <VideoModal
            visible={visible}
            videoUrl={secondaryButton?.isYoutubeVideo ? undefined : secondaryButton?.videoUrl?.url}
            youTubeId={secondaryButton?.isYoutubeVideo ? secondaryButton?.youtubeId : undefined}
            onCloseModal={() => setVisible(false)}
          />
        )}
      </div>
    </div>
  )
})

HeroBanner.displayName = 'HeroBanner'

export default withLayout(HeroBanner)
